import React, { useState, useEffect, useCallback, useMemo, useTransition } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFilter, faSync, faDownload, faChartLine, faMoneyBillWave,
    faReceipt, faTicketAlt, faPercentage, faChevronDown, faChevronUp,
    faTimes, faSearch, faCalendar, faUsers, faStore, faCreditCard, faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import {
    LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import ExportFieldSelectionModal from './ExportFieldSelectionModal';
import './PaymentAuditPage.css';
import './PaymentAuditModal.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

// "YYYY-MM" key for the current month — used as the default-expanded accordion group
// so the event-filter list opens on the month admins are most likely to want.
const getCurrentMonthKey = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const PaymentAuditPage = () => {
    const { getAccessToken } = useAuth();
    
    // Applied filters (used for API calls)
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        teamNames: [],
        statuses: [],
        paymentMethods: [],
        paymentProviders: [],
        purchaseSource: null,
        iboNumber: '',
        page: 0,
        size: 50,
        sortBy: 'createdAt',
        sortDirection: 'DESC'
    });
    
    // Pending filters (modified by user, not yet applied)
    const [pendingFilters, setPendingFilters] = useState({
        startDate: null,
        endDate: null,
        teamNames: [],
        statuses: [],
        paymentMethods: [],
        paymentProviders: [],
        purchaseSource: null,
        iboNumber: '',
        eventStartDate: null,
        eventEndDate: null
    });
    
    // Multi-event selection state (managed separately)
    const [selectedEventIds, setSelectedEventIds] = useState([]);
    const [pendingEventIds, setPendingEventIds] = useState([]);
    const [selectedMonths, setSelectedMonths] = useState(new Set());
    // Which month accordion groups are expanded in the event filter (DV-244).
    // Defaults to the current month open, all others collapsed.
    const [expandedMonths, setExpandedMonths] = useState(() => new Set([getCurrentMonthKey()]));

    // New state for enhanced features
    const [eventSearchText, setEventSearchText] = useState('');
    const [showAllEvents, setShowAllEvents] = useState(false);
    const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
    const [selectedEventForDetails, setSelectedEventForDetails] = useState(null);
    const [loadingEventDetails, setLoadingEventDetails] = useState(false);
    const [filtersChanged, setFiltersChanged] = useState(false);
    
    // State for data
    const [reportData, setReportData] = useState({ content: [], totalElements: 0, totalPages: 0 });
    const [analytics, setAnalytics] = useState(null);
    const [revenueTimeSeries, setRevenueTimeSeries] = useState([]);
    const [topEvents, setTopEvents] = useState([]);
    const [topTeams, setTopTeams] = useState([]);
    
    // State for UI
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showFilters, setShowFilters] = useState(true);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [activeTab, setActiveTab] = useState('dashboard'); // dashboard or report
    const [timePeriod, setTimePeriod] = useState('DAILY'); // DAILY, WEEKLY, MONTHLY
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [loadingPaymentDetails, setLoadingPaymentDetails] = useState(false);
    
    // Export modal state
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('CSV');

    // Transition for deferring the heavy team -> events re-filter (keeps UI responsive)
    const [isPending, startTransition] = useTransition();

    // Last Month summary section state
    const [monthlySummary, setMonthlySummary] = useState(null);
    const [loadingMonthlySummary, setLoadingMonthlySummary] = useState(false);
    const [downloadingLastMonth, setDownloadingLastMonth] = useState(false);
    
    // Available options for filters
    const [availableEvents, setAvailableEvents] = useState([]);
    const [availableTeams, setAvailableTeams] = useState([]);
    
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
    
    // Fetch available events and teams on mount
    useEffect(() => {
        fetchAvailableEvents();
        fetchAvailableTeams();
    }, []);
    
    // Fetch data when filters change
    useEffect(() => {
        if (activeTab === 'dashboard') {
            fetchAllData();
        } else {
            fetchReportData();
        }
    }, [filters, activeTab, timePeriod, selectedEventIds]);

    // Fetch the "Last Month" summary once the dashboard tab is active.
    useEffect(() => {
        if (activeTab === 'dashboard' && monthlySummary === null && !loadingMonthlySummary) {
            fetchMonthlySummary();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);
    
    const fetchAvailableEvents = async () => {
        try {
            const token = await getAccessToken();
            if (!token) {
                throw new Error('Authentication token not available');
            }
            
            const response = await fetch(`${API_BASE_URL}/em/v1/admin/events/all`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableEvents(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
        }
    };
    
    const fetchAvailableTeams = async () => {
        try {
            const token = await getAccessToken();
            if (!token) {
                throw new Error('Authentication token not available');
            }
            
            const response = await fetch(`${API_BASE_URL}/em/v1/admin/teams`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableTeams(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };
    
    const fetchMonthlySummary = async () => {
        setLoadingMonthlySummary(true);
        try {
            const token = await getAccessToken();
            if (!token) {
                throw new Error('Authentication token not available');
            }

            // Omit ?month=YYYY-MM so the backend defaults to last month.
            const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/monthly-summary`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setMonthlySummary(data.data || null);
            } else {
                setMonthlySummary(null);
            }
        } catch (error) {
            console.error('Error fetching monthly summary:', error);
            setMonthlySummary(null);
        } finally {
            setLoadingMonthlySummary(false);
        }
    };

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await Promise.all([
                fetchAnalytics(),
                fetchRevenueTimeSeries(),
                fetchTopEvents(),
                fetchTopTeams()
            ]);
            setLastRefreshed(new Date());
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    const fetchAnalytics = async () => {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');
        
        const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/analytics`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...filters,
                eventIds: selectedEventIds
            })
        });
        
        if (response.ok) {
            const data = await response.json();
            setAnalytics(data.data);
        }
    };
    
    const fetchRevenueTimeSeries = async () => {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');
        
        const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/revenue-trends?period=${timePeriod}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...filters,
                eventIds: selectedEventIds
            })
        });
        
        if (response.ok) {
            const data = await response.json();
            setRevenueTimeSeries(data.data || []);
        }
    };
    
    const fetchTopEvents = async () => {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');
        
        const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/top-events?limit=10`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...filters,
                eventIds: selectedEventIds
            })
        });
        
        if (response.ok) {
            const data = await response.json();
            setTopEvents(data.data || []);
        }
    };
    
    const fetchTopTeams = async () => {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');
        
        const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/top-teams?limit=10`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...filters,
                eventIds: selectedEventIds
            })
        });
        
        if (response.ok) {
            const data = await response.json();
            setTopTeams(data.data || []);
        }
    };
    
    const fetchReportData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const token = await getAccessToken();
            if (!token) {
                throw new Error('Authentication token not available');
            }
            
            const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/report`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...filters,
                    eventIds: selectedEventIds
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                setReportData(data.data);
                setLastRefreshed(new Date());
            } else {
                setError('Failed to load report data');
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            setError('Failed to load report data. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    const handleFilterChange = (field, value) => {
        setPendingFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setFiltersChanged(true);
    };
    
    const handleApplyFilters = () => {
        setFilters(prev => ({
            ...prev,
            startDate: pendingFilters.startDate,
            endDate: pendingFilters.endDate,
            teamNames: pendingFilters.teamNames,
            statuses: pendingFilters.statuses,
            paymentMethods: pendingFilters.paymentMethods,
            paymentProviders: pendingFilters.paymentProviders,
            purchaseSource: pendingFilters.purchaseSource,
            iboNumber: pendingFilters.iboNumber,
            page: 0 // Reset to first page when filters change
        }));
        setSelectedEventIds(pendingEventIds);
        setFiltersChanged(false);
    };
    
    const handleEventSelection = (eventId, isChecked) => {
        setPendingEventIds(prev => {
            if (isChecked) {
                return [...prev, eventId];
            } else {
                return prev.filter(id => id !== eventId);
            }
        });
        setFiltersChanged(true);
    };
    
    const handleMonthSelection = (monthKey, eventIds, isChecked) => {
        setPendingEventIds(prev => {
            if (isChecked) {
                const newIds = eventIds.filter(id => !prev.includes(id));
                return [...prev, ...newIds];
            } else {
                return prev.filter(id => !eventIds.includes(id));
            }
        });
        
        setSelectedMonths(prev => {
            const newSet = new Set(prev);
            if (isChecked) {
                newSet.add(monthKey);
            } else {
                newSet.delete(monthKey);
            }
            return newSet;
        });
        setFiltersChanged(true);
    };

    // Accordion (DV-244): expand/collapse a single month group in the event filter.
    const toggleMonthExpanded = (monthKey) => {
        setExpandedMonths(prev => {
            const newSet = new Set(prev);
            if (newSet.has(monthKey)) {
                newSet.delete(monthKey);
            } else {
                newSet.add(monthKey);
            }
            return newSet;
        });
    };

    const expandAllMonths = (monthKeys) => setExpandedMonths(new Set(monthKeys));
    const collapseAllMonths = () => setExpandedMonths(new Set());

    const handleTeamSelection = (teamName, isChecked) => {
        setFiltersChanged(true);
        // Defer the state update that triggers the heavy events re-filter so the
        // checkbox stays responsive; isPending drives the "Filtering events..." spinner.
        startTransition(() => {
            setPendingFilters(prev => {
                const newTeamNames = isChecked
                    ? [...prev.teamNames, teamName]
                    : prev.teamNames.filter(name => name !== teamName);
                return { ...prev, teamNames: newTeamNames };
            });
        });
    };
    
    const handleResetFilters = () => {
        const resetFilters = {
            startDate: null,
            endDate: null,
            teamNames: [],
            statuses: [],
            paymentMethods: [],
            paymentProviders: [],
            purchaseSource: null,
            iboNumber: '',
            eventStartDate: null,
            eventEndDate: null
        };
        setPendingFilters(resetFilters);
        setFilters({
            ...resetFilters,
            page: 0,
            size: 50,
            sortBy: 'createdAt',
            sortDirection: 'DESC'
        });
        setSelectedEventIds([]);
        setPendingEventIds([]);
        setSelectedMonths(new Set());
        setExpandedMonths(new Set([getCurrentMonthKey()]));
        setEventSearchText('');
        setFiltersChanged(false);
    };
    
    const handleShowExportModal = (format) => {
        setExportFormat(format);
        setShowExportModal(true);
    };
    
    // Trigger a browser download for an export blob response.
    const triggerBlobDownload = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    // Shared export request: POSTs to the export endpoint and downloads the resulting blob.
    // Returns true on success, false (and sets error) otherwise.
    const requestExportDownload = async (exportRequest, filename) => {
        const token = await getAccessToken();
        if (!token) {
            throw new Error('Authentication token not available');
        }

        const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/export`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(exportRequest)
        });

        if (response.ok) {
            const blob = await response.blob();
            triggerBlobDownload(blob, filename);
            return true;
        } else {
            const errorText = await response.text();
            setError(`Failed to export report: ${errorText}`);
            return false;
        }
    };

    const handleExport = async (selectedFields, format) => {
        try {
            setLoading(true);
            setShowExportModal(false); // Close modal

            const exportRequest = {
                filters: {
                    ...filters,
                    eventIds: selectedEventIds
                },
                selectedFields: selectedFields,
                format: format
            };

            const extension = format.toLowerCase() === 'excel' ? 'xlsx' : 'csv';
            await requestExportDownload(exportRequest, `payment-report-${Date.now()}.${extension}`);
        } catch (error) {
            console.error('Error exporting report:', error);
            setError(`Export failed: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Download a team-wise CSV report (one row per team) for last month.
    const handleDownloadLastMonthReport = async () => {
        try {
            setDownloadingLastMonth(true);
            setError(null);

            // Prefer the month the summary is for; else compute last month.
            const now = new Date();
            const fallback = `${now.getUTCFullYear()}-${String(now.getUTCMonth()).padStart(2, '0')}`; // last month
            const monthLabel = (monthlySummary && monthlySummary.month) ? monthlySummary.month : fallback;

            const token = await getAccessToken();
            if (!token) {
                throw new Error('Authentication token not available');
            }

            // Team-wise summary report (one row per team) for the month.
            const response = await fetch(
                `${API_BASE_URL}/em/v1/admin/payment-audit/monthly-team-report?month=${encodeURIComponent(monthLabel)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `HTTP ${response.status}`);
            }
            const blob = await response.blob();
            triggerBlobDownload(blob, `last-month-team-report-${monthLabel}.csv`);
        } catch (error) {
            console.error('Error downloading last month team report:', error);
            setError(`Export failed: ${error.message}`);
        } finally {
            setDownloadingLastMonth(false);
        }
    };
    
    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };
    
    const handleRowClick = async (paymentId) => {
        setLoadingPaymentDetails(true);
        setShowPaymentModal(true);
        
        try {
            const token = await getAccessToken();
            if (!token) {
                throw new Error('Authentication token not available');
            }
            
            const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/${paymentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setSelectedPayment(data.data);
            } else {
                setError('Failed to load payment details');
            }
        } catch (error) {
            console.error('Error fetching payment details:', error);
            setError('Failed to load payment details. Please try again.');
        } finally {
            setLoadingPaymentDetails(false);
        }
    };
    
    const handleCloseModal = () => {
        setShowPaymentModal(false);
        setSelectedPayment(null);
    };
    
    const handleDownloadInvoice = async () => {
        // Use the admin payment audit API to fetch invoice URL from S3
        if (!selectedPayment || !selectedPayment.id) {
            setError('Payment information not available');
            return;
        }
        
        try {
            setLoadingPaymentDetails(true);
            const token = await getAccessToken();
            if (!token) {
                throw new Error('Authentication token not available');
            }
            
            // Call the admin payment audit invoice API to get the S3 URL
            const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/${selectedPayment.id}/invoice`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                let invoiceUrl = data.data;
                
                // Check if it's an S3 URL or a backend API URL
                if (invoiceUrl) {
                    // If it's a backend API invoice endpoint, convert to the files endpoint
                    if (invoiceUrl.includes('/em/v1/invoices/') && !invoiceUrl.includes('/files/')) {
                        // Extract invoice ID from URL
                        const invoiceIdMatch = invoiceUrl.match(/\/invoices\/(INV-[^/]+)/);
                        if (invoiceIdMatch) {
                            const invoiceId = invoiceIdMatch[1];
                            // Use the files endpoint which directly returns the PDF
                            invoiceUrl = `${API_BASE_URL}/em/v1/invoices/files/${invoiceId}.pdf`;
                        }
                    } 
                    // If the URL is relative or doesn't start with http, make it absolute
                    else if (!invoiceUrl.startsWith('http')) {
                        // Remove leading slash if present
                        invoiceUrl = invoiceUrl.startsWith('/') ? invoiceUrl.substring(1) : invoiceUrl;
                        invoiceUrl = `${API_BASE_URL}/${invoiceUrl}`;
                    }
                    
                    // Open the invoice URL in a new tab
                    // Use window.open with noopener for security
                    const newWindow = window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
                    if (!newWindow) {
                        // If popup was blocked, try alternative method
                        const link = document.createElement('a');
                        link.href = invoiceUrl;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        link.download = selectedPayment.invoiceNumber || 'invoice.pdf';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                } else {
                    setError('Invoice URL not available');
                }
            } else {
                const errorData = await response.json();
                setError(errorData.message || 'Failed to fetch invoice');
            }
        } catch (error) {
            console.error('Error fetching invoice:', error);
            setError('Failed to open invoice. Please try again.');
        } finally {
            setLoadingPaymentDetails(false);
        }
    };
    
    const handleRefreshFees = async (paymentId) => {
        try {
            setLoadingPaymentDetails(true);
            const token = await getAccessToken();
            if (!token) {
                throw new Error('Authentication token not available');
            }
            
            const response = await fetch(`${API_BASE_URL}/em/v1/admin/payment-audit/${paymentId}/refresh-fees`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                // Reload payment details to show updated fees
                await handleRowClick(paymentId);
            } else {
                const errorData = await response.json();
                setError(errorData.message || 'Failed to refresh fees');
            }
        } catch (error) {
            console.error('Error refreshing fees:', error);
            setError('Failed to refresh fees. Please try again.');
        } finally {
            setLoadingPaymentDetails(false);
        }
    };
    
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    // Turn a "YYYY-MM" month key into a readable "Month YYYY" label.
    const formatMonthLabel = (month) => {
        if (!month) return '';
        const parts = month.split('-');
        if (parts.length < 2) return month;
        const year = parseInt(parts[0], 10);
        const monthIdx = parseInt(parts[1], 10) - 1;
        if (isNaN(year) || isNaN(monthIdx)) return month;
        const date = new Date(year, monthIdx, 1);
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };
    
    const formatEventDate = (dateStr, timezone) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return 'N/A';
            if (timezone) {
                return date.toLocaleString('en-US', {
                    timeZone: timezone,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                    timeZoneName: 'short'
                });
            }
            return date.toLocaleString();
        } catch (e) {
            return dateStr;
        }
    };
    
    // Precompute an event.id -> eligible-teams lookup once per events change so the
    // per-render eligibility check is O(1) instead of re-parsing eligibleTeams every time.
    // Value is either the string 'all' (open to everyone) or a Set of team names.
    const eventEligibilityLookup = useMemo(() => {
        const lookup = {};
        availableEvents.forEach(event => {
            if (!event || event.id == null) return;
            const raw = event.eligibleTeams;
            if (!raw) {
                lookup[event.id] = 'all';
                return;
            }
            if (Array.isArray(raw)) {
                if (raw.length === 0 || raw.includes('all')) {
                    lookup[event.id] = 'all';
                } else {
                    lookup[event.id] = new Set(raw.map(t => t.trim()));
                }
            } else if (typeof raw === 'string') {
                if (raw === 'all') {
                    lookup[event.id] = 'all';
                } else {
                    lookup[event.id] = new Set(raw.split(',').map(t => t.trim()));
                }
            } else {
                lookup[event.id] = 'all';
            }
        });
        return lookup;
    }, [availableEvents]);

    // Helper function to check if event is eligible for selected teams (O(1) via lookup)
    const isEventEligibleForTeams = useCallback((event, selectedTeams) => {
        if (!selectedTeams || selectedTeams.length === 0) return true;
        const eligible = eventEligibilityLookup[event.id];
        if (!eligible || eligible === 'all') return true;
        return selectedTeams.some(team => eligible.has(team));
    }, [eventEligibilityLookup]);
    
    // Get filtered events based on team eligibility, search text, and date range
    const getFilteredEvents = useMemo(() => {
        let filtered = [...availableEvents];
        
        // Filter by team eligibility
        if (!showAllEvents && pendingFilters.teamNames.length > 0) {
            filtered = filtered.filter(event => 
                isEventEligibleForTeams(event, pendingFilters.teamNames)
            );
        }
        
        // Filter by search text
        if (eventSearchText.trim()) {
            const searchLower = eventSearchText.toLowerCase();
            filtered = filtered.filter(event =>
                event.name.toLowerCase().includes(searchLower)
            );
        }
        
        // Filter by event date range
        if (pendingFilters.eventStartDate || pendingFilters.eventEndDate) {
            const startDate = pendingFilters.eventStartDate ? new Date(pendingFilters.eventStartDate) : null;
            const endDate = pendingFilters.eventEndDate ? new Date(pendingFilters.eventEndDate) : null;
            
            filtered = filtered.filter(event => {
                if (!event.startDate) return false;
                const eventDate = new Date(event.startDate);
                
                if (startDate && eventDate < startDate) return false;
                if (endDate && eventDate > endDate) return false;
                return true;
            });
        }
        
        return filtered;
    }, [availableEvents, pendingFilters.teamNames, pendingFilters.eventStartDate, pendingFilters.eventEndDate, eventSearchText, showAllEvents, isEventEligibleForTeams]);
    
    // Auto-select events when date range changes
    useEffect(() => {
        if (pendingFilters.eventStartDate || pendingFilters.eventEndDate) {
            const matchingEventIds = getFilteredEvents
                .filter(event => {
                    const startDate = pendingFilters.eventStartDate ? new Date(pendingFilters.eventStartDate) : null;
                    const endDate = pendingFilters.eventEndDate ? new Date(pendingFilters.eventEndDate) : null;
                    const eventDate = new Date(event.startDate);
                    
                    if (startDate && eventDate < startDate) return false;
                    if (endDate && eventDate > endDate) return false;
                    return true;
                })
                .map(event => event.id);
            
            setPendingEventIds(matchingEventIds);
            setFiltersChanged(true);
        }
    }, [pendingFilters.eventStartDate, pendingFilters.eventEndDate, getFilteredEvents]);
    
    // Month-grouped + sorted structure for the events dropdown.
    // Memoized on (getFilteredEvents, eventSearchText) so it isn't rebuilt on unrelated renders.
    // Sort order relative to the current month:
    //   1. current month first
    //   2. then recent past months, most-recent-first (descending)
    //   3. then upcoming months, soonest-first (ascending)
    const groupedSortedMonths = useMemo(() => {
        const now = new Date();
        const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const groupedEvents = {};

        getFilteredEvents.forEach(event => {
            if (!event.startDate) return;
            const eventDate = new Date(event.startDate);
            const sortKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
            const monthYear = eventDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

            if (!groupedEvents[sortKey]) {
                groupedEvents[sortKey] = {
                    label: monthYear,
                    events: [],
                    isFuture: eventDate >= now
                };
            }

            groupedEvents[sortKey].events.push({ ...event, eventDate });
        });

        Object.values(groupedEvents).forEach(group => {
            group.events.sort((a, b) => a.eventDate - b.eventDate);
        });

        // rank: current month = 0; past months = 1 (most-recent-first); future months = 2 (soonest-first)
        const rankOf = (key) => {
            if (key === currentKey) return 0;
            return key < currentKey ? 1 : 2;
        };

        const sortedMonths = Object.keys(groupedEvents).sort((a, b) => {
            const rankA = rankOf(a);
            const rankB = rankOf(b);
            if (rankA !== rankB) return rankA - rankB;
            if (rankA === 1) return b.localeCompare(a); // past: descending (most recent first)
            return a.localeCompare(b); // current (single key) or future: ascending (soonest first)
        });

        return { groupedEvents, sortedMonths };
    }, [getFilteredEvents, eventSearchText]);

    // Fetch event details for modal
    const handleShowEventDetails = async (eventId) => {
        setLoadingEventDetails(true);
        setShowEventDetailsModal(true);
        setSelectedEventForDetails(null);
        
        try {
            const token = await getAccessToken();
            if (!token) {
                throw new Error('Authentication token not available');
            }
            
            const response = await fetch(`${API_BASE_URL}/em/v1/events/${eventId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setSelectedEventForDetails(data.data);
            } else {
                setError('Failed to load event details');
            }
        } catch (error) {
            console.error('Error fetching event details:', error);
            setError('Failed to load event details. Please try again.');
        } finally {
            setLoadingEventDetails(false);
        }
    };
    
    const handleCloseEventDetailsModal = () => {
        setShowEventDetailsModal(false);
        setSelectedEventForDetails(null);
    };
    
    // Generate page numbers for pagination
    const generatePageNumbers = () => {
        const totalPages = reportData.totalPages;
        const currentPage = filters.page;
        const pages = [];
        
        if (totalPages <= 10) {
            // Show all pages if 10 or fewer
            for (let i = 0; i < totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(0);
            
            // Determine range around current page
            let startPage = Math.max(1, currentPage - 1);
            let endPage = Math.min(totalPages - 2, currentPage + 1);
            
            // Adjust if near start
            if (currentPage <= 2) {
                endPage = Math.min(4, totalPages - 2);
            }
            
            // Adjust if near end
            if (currentPage >= totalPages - 3) {
                startPage = Math.max(1, totalPages - 5);
            }
            
            // Add ellipsis after first page if needed
            if (startPage > 1) {
                pages.push('ellipsis-start');
            }
            
            // Add middle pages
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            
            // Add ellipsis before last page if needed
            if (endPage < totalPages - 2) {
                pages.push('ellipsis-end');
            }
            
            // Always show last page
            pages.push(totalPages - 1);
        }
        
        return pages;
    };
    
    // Prepare chart data
    const paymentMethodChartData = useMemo(() => {
        if (!analytics || !analytics.revenueByPaymentMethod) return [];
        return Object.entries(analytics.revenueByPaymentMethod).map(([name, value]) => ({
            name,
            value: parseFloat(value)
        }));
    }, [analytics]);
    
    return (
        <div className="payment-audit-page">
            <div className="page-header">
                <h1>Payment Audit & Analytics</h1>
                <div className="header-actions">
                    {lastRefreshed && (
                        <span className="last-refreshed">
                            Last updated: {lastRefreshed.toLocaleTimeString()}
                        </span>
                    )}
                    <button
                        className="btn-refresh"
                        onClick={() => activeTab === 'dashboard' ? fetchAllData() : fetchReportData()}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh
                    </button>
                </div>
            </div>
            
            {/* Filter Panel */}
            <div className={`filter-panel ${showFilters ? 'expanded' : 'collapsed'}`}>
                <div className="filter-header" onClick={() => setShowFilters(!showFilters)}>
                    <h3>
                        <FontAwesomeIcon icon={faFilter} /> Filters
                    </h3>
                    <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} />
                </div>
                
                {showFilters && (
                    <div className="filter-content">
                        {filtersChanged && (
                            <div className="filter-changed-banner">
                                <span>Filters have been modified. Click "Apply Filters" to update results.</span>
                            </div>
                        )}
                        
                        <div className="filter-row">
                            <div className="filter-group">
                                <label><FontAwesomeIcon icon={faCalendar} /> Payment Start Date</label>
                                <input
                                    type="date"
                                    value={pendingFilters.startDate ? new Date(pendingFilters.startDate).toISOString().split('T')[0] : ''}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                />
                            </div>
                            
                            <div className="filter-group">
                                <label><FontAwesomeIcon icon={faCalendar} /> Payment End Date</label>
                                <input
                                    type="date"
                                    value={pendingFilters.endDate ? new Date(pendingFilters.endDate).toISOString().split('T')[0] : ''}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                />
                            </div>
                            
                            <div className="filter-group">
                                <label><FontAwesomeIcon icon={faSearch} /> IBO Number</label>
                                <input
                                    type="text"
                                    placeholder="Search IBO..."
                                    value={pendingFilters.iboNumber}
                                    onChange={(e) => handleFilterChange('iboNumber', e.target.value)}
                                />
                            </div>
                        </div>
                        
                        <div className="filter-row">
                            <div className="filter-group">
                                <label><FontAwesomeIcon icon={faCalendar} /> Event Start Date</label>
                                <input
                                    type="date"
                                    value={pendingFilters.eventStartDate ? new Date(pendingFilters.eventStartDate).toISOString().split('T')[0] : ''}
                                    onChange={(e) => handleFilterChange('eventStartDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                />
                            </div>
                            
                            <div className="filter-group">
                                <label><FontAwesomeIcon icon={faCalendar} /> Event End Date</label>
                                <input
                                    type="date"
                                    value={pendingFilters.eventEndDate ? new Date(pendingFilters.eventEndDate).toISOString().split('T')[0] : ''}
                                    onChange={(e) => handleFilterChange('eventEndDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                />
                            </div>
                        </div>
                        
                        {(pendingFilters.eventStartDate || pendingFilters.eventEndDate) && (
                            <div className="event-date-info">
                                <span>
                                    {pendingEventIds.length} event(s) auto-selected based on date range
                                </span>
                            </div>
                        )}
                        
                        <div className="filter-row">
                            <div className="filter-group full-width">
                                <label><FontAwesomeIcon icon={faUsers} /> Teams (Select Multiple)</label>
                                <div className="team-filter-container">
                                    <div className="team-filter-actions">
                                        <button
                                            type="button"
                                            className="team-action-btn"
                                            onClick={() => {
                                                setPendingFilters(prev => ({
                                                    ...prev,
                                                    teamNames: availableTeams.map(t => t.name)
                                                }));
                                                setFiltersChanged(true);
                                            }}
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            className="team-action-btn"
                                            onClick={() => {
                                                setPendingFilters(prev => ({ ...prev, teamNames: [] }));
                                                setFiltersChanged(true);
                                            }}
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                    <div className="team-checkbox-list">
                                        {availableTeams.map(team => (
                                            <label key={team.name} className="team-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={pendingFilters.teamNames.includes(team.name)}
                                                    onChange={(e) => handleTeamSelection(team.name, e.target.checked)}
                                                />
                                                <span className="team-name">{team.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {pendingFilters.teamNames.length > 0 && (
                                        <div className="selected-teams-summary">
                                            {pendingFilters.teamNames.length} team{pendingFilters.teamNames.length !== 1 ? 's' : ''} selected
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="filter-row">
                            <div className="filter-group full-width">
                                <label><FontAwesomeIcon icon={faTicketAlt} /> Events (Select Multiple)</label>
                                
                                {/* Event Search Bar */}
                                <div className="event-search-bar">
                                    <input
                                        type="text"
                                        placeholder="Search events by name..."
                                        value={eventSearchText}
                                        onChange={(e) => setEventSearchText(e.target.value)}
                                        className="event-search-input"
                                    />
                                    {eventSearchText && (
                                        <button
                                            type="button"
                                            className="clear-search-btn"
                                            onClick={() => setEventSearchText('')}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                </div>
                                
                                {/* Team eligibility toggle */}
                                {pendingFilters.teamNames.length > 0 && (
                                    <div className="event-eligibility-toggle">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={showAllEvents}
                                                onChange={(e) => setShowAllEvents(e.target.checked)}
                                            />
                                            Show all events (including non-eligible for selected teams)
                                        </label>
                                    </div>
                                )}
                                
                                {/* Event count info */}
                                <div className="event-filter-info">
                                    Showing {getFilteredEvents.length} of {availableEvents.length} events
                                    {eventSearchText && ` matching "${eventSearchText}"`}
                                    {isPending && (
                                        <span className="events-filtering-indicator">
                                            <span className="loading-spinner loading-spinner-inline"></span>
                                            Filtering events…
                                        </span>
                                    )}
                                </div>

                                {/* Accordion expand/collapse all (DV-244) — hidden while searching, since search auto-expands all groups */}
                                {groupedSortedMonths.sortedMonths.length > 1 && !eventSearchText.trim() && (
                                    <div className="event-accordion-actions">
                                        <button
                                            type="button"
                                            className="accordion-action-btn"
                                            onClick={() => expandAllMonths(groupedSortedMonths.sortedMonths)}
                                        >
                                            Expand all
                                        </button>
                                        <button
                                            type="button"
                                            className="accordion-action-btn"
                                            onClick={collapseAllMonths}
                                        >
                                            Collapse all
                                        </button>
                                    </div>
                                )}

                                <div className={`event-filter-container ${isPending ? 'is-filtering' : ''}`}>
                                    {(() => {
                                        const { groupedEvents, sortedMonths } = groupedSortedMonths;
                                        // While searching, force every group open so matching events aren't hidden inside a collapsed month.
                                        const searchActive = eventSearchText.trim().length > 0;

                                        if (sortedMonths.length === 0) {
                                            return (
                                                <div className="no-events-message">
                                                    No events found matching the current filters.
                                                </div>
                                            );
                                        }
                                        
                                        return sortedMonths.map(sortKey => {
                                            const group = groupedEvents[sortKey];
                                            const monthEventIds = group.events.map(e => e.id);
                                            const allSelected = monthEventIds.every(id => pendingEventIds.includes(id));
                                            const someSelected = monthEventIds.some(id => pendingEventIds.includes(id));
                                            const expanded = searchActive || expandedMonths.has(sortKey);

                                            return (
                                                <div key={sortKey} className={`event-month-group ${expanded ? 'is-expanded' : 'is-collapsed'}`}>
                                                    <div className="month-header">
                                                        <input
                                                            type="checkbox"
                                                            className="month-select-checkbox"
                                                            checked={allSelected}
                                                            ref={el => {
                                                                if (el) el.indeterminate = someSelected && !allSelected;
                                                            }}
                                                            onChange={(e) => handleMonthSelection(sortKey, monthEventIds, e.target.checked)}
                                                            title="Select all events in this month"
                                                        />
                                                        <button
                                                            type="button"
                                                            className="month-toggle-btn"
                                                            onClick={() => toggleMonthExpanded(sortKey)}
                                                            aria-expanded={expanded}
                                                            title={expanded ? 'Collapse month' : 'Expand month'}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={expanded ? faChevronUp : faChevronDown}
                                                                className="month-toggle-chevron"
                                                            />
                                                            <strong>
                                                                {group.isFuture ? '📅' : '📌'} {group.label} ({group.events.length} events)
                                                            </strong>
                                                        </button>
                                                    </div>
                                                    {expanded && (
                                                    <div className="event-list">
                                                        {group.events.map(event => (
                                                            <div key={event.id} className="event-checkbox-row">
                                                                <label className="event-checkbox-label">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={pendingEventIds.includes(event.id)}
                                                                        onChange={(e) => handleEventSelection(event.id, e.target.checked)}
                                                                    />
                                                                    <span className="event-name">{event.name}</span>
                                                                    <span className="event-meta">
                                                                        {event.eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                                    </span>
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    className="event-info-btn"
                                                                    onClick={() => handleShowEventDetails(event.id)}
                                                                    title="View event details"
                                                                >
                                                                    ⓘ
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                                {pendingEventIds.length > 0 && (
                                    <div className="selected-events-summary">
                                        {pendingEventIds.length} event{pendingEventIds.length !== 1 ? 's' : ''} selected
                                        <button 
                                            type="button" 
                                            className="clear-selection-btn"
                                            onClick={() => {
                                                setPendingEventIds([]);
                                                setFiltersChanged(true);
                                            }}
                                        >
                                            Clear Selection
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="filter-row">
                            <div className="filter-group">
                                <label>Status</label>
                                <div className="checkbox-group">
                                    {['SUCCESS', 'CASH_PAYMENT', 'FAILED', 'PENDING', 'REFUNDED'].map(status => (
                                        <label key={status}>
                                            <input
                                                type="checkbox"
                                                checked={pendingFilters.statuses.includes(status)}
                                                onChange={(e) => {
                                                    const newStatuses = e.target.checked
                                                        ? [...pendingFilters.statuses, status]
                                                        : pendingFilters.statuses.filter(s => s !== status);
                                                    handleFilterChange('statuses', newStatuses);
                                                }}
                                            />
                                            {status}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="filter-group">
                                <label><FontAwesomeIcon icon={faCreditCard} /> Payment Method</label>
                                <div className="checkbox-group">
                                    {['CARD', 'CASH', 'SQUARE'].map(method => (
                                        <label key={method}>
                                            <input
                                                type="checkbox"
                                                checked={pendingFilters.paymentMethods.includes(method)}
                                                onChange={(e) => {
                                                    const newMethods = e.target.checked
                                                        ? [...pendingFilters.paymentMethods, method]
                                                        : pendingFilters.paymentMethods.filter(m => m !== method);
                                                    handleFilterChange('paymentMethods', newMethods);
                                                }}
                                            />
                                            {method}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            
                            <div className="filter-group">
                                <label><FontAwesomeIcon icon={faStore} /> Purchase Source</label>
                                <div className="radio-group">
                                    <label>
                                        <input
                                            type="radio"
                                            checked={pendingFilters.purchaseSource === null}
                                            onChange={() => handleFilterChange('purchaseSource', null)}
                                        />
                                        All
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            checked={pendingFilters.purchaseSource === 'ADMIN_PORTAL'}
                                            onChange={() => handleFilterChange('purchaseSource', 'ADMIN_PORTAL')}
                                        />
                                        Admin Portal
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            checked={pendingFilters.purchaseSource === 'CUSTOMER_WEBSITE'}
                                            onChange={() => handleFilterChange('purchaseSource', 'CUSTOMER_WEBSITE')}
                                        />
                                        Customer Website
                                    </label>
                                </div>
                            </div>
                        </div>
                        
                        {/* Active Filter Pills */}
                        {(filters.teamNames.length > 0 || selectedEventIds.length > 0 || filters.startDate || filters.endDate || filters.iboNumber || filters.statuses.length > 0 || filters.paymentMethods.length > 0 || filters.purchaseSource) && (
                            <div className="active-filters-pills">
                                <div className="pills-header">Active Filters:</div>
                                <div className="pills-container">
                                    {filters.teamNames.map(team => (
                                        <span key={`team-${team}`} className="filter-pill">
                                            Team: {team}
                                            <button onClick={() => {
                                                const newTeams = filters.teamNames.filter(t => t !== team);
                                                setFilters(prev => ({ ...prev, teamNames: newTeams, page: 0 }));
                                                setPendingFilters(prev => ({ ...prev, teamNames: newTeams }));
                                            }}>×</button>
                                        </span>
                                    ))}
                                    {selectedEventIds.length > 0 && (
                                        <span className="filter-pill">
                                            {selectedEventIds.length} Event(s)
                                            <button onClick={() => {
                                                setSelectedEventIds([]);
                                                setPendingEventIds([]);
                                            }}>×</button>
                                        </span>
                                    )}
                                    {filters.startDate && (
                                        <span className="filter-pill">
                                            From: {new Date(filters.startDate).toLocaleDateString()}
                                            <button onClick={() => {
                                                setFilters(prev => ({ ...prev, startDate: null, page: 0 }));
                                                setPendingFilters(prev => ({ ...prev, startDate: null }));
                                            }}>×</button>
                                        </span>
                                    )}
                                    {filters.endDate && (
                                        <span className="filter-pill">
                                            To: {new Date(filters.endDate).toLocaleDateString()}
                                            <button onClick={() => {
                                                setFilters(prev => ({ ...prev, endDate: null, page: 0 }));
                                                setPendingFilters(prev => ({ ...prev, endDate: null }));
                                            }}>×</button>
                                        </span>
                                    )}
                                    {filters.iboNumber && (
                                        <span className="filter-pill">
                                            IBO: {filters.iboNumber}
                                            <button onClick={() => {
                                                setFilters(prev => ({ ...prev, iboNumber: '', page: 0 }));
                                                setPendingFilters(prev => ({ ...prev, iboNumber: '' }));
                                            }}>×</button>
                                        </span>
                                    )}
                                    {filters.statuses.map(status => (
                                        <span key={`status-${status}`} className="filter-pill">
                                            Status: {status}
                                            <button onClick={() => {
                                                const newStatuses = filters.statuses.filter(s => s !== status);
                                                setFilters(prev => ({ ...prev, statuses: newStatuses, page: 0 }));
                                                setPendingFilters(prev => ({ ...prev, statuses: newStatuses }));
                                            }}>×</button>
                                        </span>
                                    ))}
                                    {filters.paymentMethods.map(method => (
                                        <span key={`method-${method}`} className="filter-pill">
                                            Method: {method}
                                            <button onClick={() => {
                                                const newMethods = filters.paymentMethods.filter(m => m !== method);
                                                setFilters(prev => ({ ...prev, paymentMethods: newMethods, page: 0 }));
                                                setPendingFilters(prev => ({ ...prev, paymentMethods: newMethods }));
                                            }}>×</button>
                                        </span>
                                    ))}
                                    {filters.purchaseSource && (
                                        <span className="filter-pill">
                                            Source: {filters.purchaseSource === 'ADMIN_PORTAL' ? 'Admin' : 'Customer'}
                                            <button onClick={() => {
                                                setFilters(prev => ({ ...prev, purchaseSource: null, page: 0 }));
                                                setPendingFilters(prev => ({ ...prev, purchaseSource: null }));
                                            }}>×</button>
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        <div className="filter-actions">
                            <button className="btn-reset" onClick={handleResetFilters}>
                                <FontAwesomeIcon icon={faTimes} /> Reset Filters
                            </button>
                            <button 
                                className={`btn-apply-filters ${filtersChanged ? 'filters-changed' : ''}`}
                                onClick={handleApplyFilters}
                                disabled={!filtersChanged}
                            >
                                <FontAwesomeIcon icon={faSearch} /> Apply Filters
                            </button>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Tab Navigation */}
            <div className="tab-navigation">
                <button
                    className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                    onClick={() => setActiveTab('dashboard')}
                >
                    <FontAwesomeIcon icon={faChartLine} /> Dashboard
                </button>
                <button
                    className={`tab ${activeTab === 'report' ? 'active' : ''}`}
                    onClick={() => setActiveTab('report')}
                >
                    <FontAwesomeIcon icon={faReceipt} /> Detailed Report
                </button>
            </div>
            
            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}
            
            {loading && activeTab === 'dashboard' ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading analytics...</p>
                </div>
            ) : (
                <>
                    {activeTab === 'dashboard' && analytics && (
                        <div className="dashboard-section">
                            {/* Last Month Summary */}
                            <div className="last-month-section">
                                <div className="last-month-header">
                                    <h3>
                                        <FontAwesomeIcon icon={faCalendar} /> Last Month
                                        {monthlySummary && monthlySummary.month && (
                                            <span className="last-month-label"> — {formatMonthLabel(monthlySummary.month)}</span>
                                        )}
                                    </h3>
                                    <button
                                        className="btn-export btn-download-last-month"
                                        onClick={handleDownloadLastMonthReport}
                                        disabled={downloadingLastMonth}
                                    >
                                        <FontAwesomeIcon icon={faDownload} spin={downloadingLastMonth} />
                                        {downloadingLastMonth ? ' Preparing…' : ' Download team report'}
                                    </button>
                                </div>

                                {loadingMonthlySummary ? (
                                    <div className="last-month-loading">
                                        <span className="loading-spinner loading-spinner-inline"></span>
                                        Loading last month summary…
                                    </div>
                                ) : monthlySummary ? (
                                    <>
                                        <div className="last-month-cards">
                                            <div className="last-month-card">
                                                <span className="lm-card-label">Events</span>
                                                <span className="lm-card-value">{monthlySummary.eventsCount ?? 0}</span>
                                            </div>
                                            <div className="last-month-card">
                                                <span className="lm-card-label">Revenue</span>
                                                <span className="lm-card-value">{formatCurrency(monthlySummary.totalRevenue)}</span>
                                            </div>
                                            <div className="last-month-card">
                                                <span className="lm-card-label">Transactions</span>
                                                <span className="lm-card-value">{monthlySummary.totalTransactions ?? 0}</span>
                                            </div>
                                            <div className="last-month-card">
                                                <span className="lm-card-label">Tickets</span>
                                                <span className="lm-card-value">{monthlySummary.totalTicketsSold ?? 0}</span>
                                            </div>
                                            <div className="last-month-card lm-top-team">
                                                <span className="lm-card-label">Top team</span>
                                                {monthlySummary.topTeams && monthlySummary.topTeams.length > 0 ? (
                                                    <span className="lm-card-value">
                                                        {monthlySummary.topTeams[0].name}
                                                        <span className="lm-card-sub"> · {formatCurrency(monthlySummary.topTeams[0].revenue)}</span>
                                                    </span>
                                                ) : (
                                                    <span className="lm-card-value">N/A</span>
                                                )}
                                            </div>
                                        </div>

                                        {monthlySummary.topEvents && monthlySummary.topEvents.length > 0 && (
                                            <div className="last-month-top-events">
                                                <h4>Top events</h4>
                                                <ul>
                                                    {monthlySummary.topEvents.slice(0, 5).map((event, index) => (
                                                        <li key={event.id || index}>
                                                            <span className="lm-event-name">{event.name}</span>
                                                            <span className="lm-event-revenue">{formatCurrency(event.revenue)}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="last-month-empty">
                                        No summary data available for last month.
                                    </div>
                                )}
                            </div>

                            {/* Summary Cards */}
                            <div className="summary-cards">
                                <div className="summary-card revenue">
                                    <div className="card-icon">
                                        <FontAwesomeIcon icon={faMoneyBillWave} />
                                    </div>
                                    <div className="card-content">
                                        <h3>Total Revenue</h3>
                                        <p className="card-value">{formatCurrency(analytics.totalRevenue)}</p>
                                    </div>
                                </div>
                                
                                <div className="summary-card transactions">
                                    <div className="card-icon">
                                        <FontAwesomeIcon icon={faReceipt} />
                                    </div>
                                    <div className="card-content">
                                        <h3>Total Transactions</h3>
                                        <p className="card-value">{analytics.totalTransactions}</p>
                                    </div>
                                </div>
                                
                                <div className="summary-card tickets">
                                    <div className="card-icon">
                                        <FontAwesomeIcon icon={faTicketAlt} />
                                    </div>
                                    <div className="card-content">
                                        <h3>Tickets Sold</h3>
                                        <p className="card-value">{analytics.totalTicketsSold}</p>
                                    </div>
                                </div>
                                
                                <div className="summary-card success-rate">
                                    <div className="card-icon">
                                        <FontAwesomeIcon icon={faPercentage} />
                                    </div>
                                    <div className="card-content">
                                        <h3>Success Rate</h3>
                                        <p className="card-value">{analytics.successRate?.toFixed(1)}%</p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Charts Row */}
                            <div className="charts-row">
                                <div className="chart-container">
                                    <div className="chart-header">
                                        <h3>Revenue Trend</h3>
                                        <select value={timePeriod} onChange={(e) => setTimePeriod(e.target.value)}>
                                            <option value="DAILY">Daily</option>
                                            <option value="WEEKLY">Weekly</option>
                                            <option value="MONTHLY">Monthly</option>
                                        </select>
                                    </div>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={revenueTimeSeries}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Line type="monotone" dataKey="revenue" stroke="#8884d8" name="Revenue" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                                
                                <div className="chart-container">
                                    <h3>Payment Method Breakdown</h3>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={paymentMethodChartData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {paymentMethodChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => formatCurrency(value)} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            
                            {/* Top Performers Row */}
                            <div className="top-performers-row">
                                <div className="top-performers-table">
                                    <h3>Top Performing Events</h3>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Event Name</th>
                                                <th>Revenue</th>
                                                <th>Transactions</th>
                                                <th>Tickets Sold</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topEvents.map((event, index) => (
                                                <tr key={index}>
                                                    <td>{event.name}</td>
                                                    <td>{formatCurrency(event.revenue)}</td>
                                                    <td>{event.transactionCount}</td>
                                                    <td>{event.ticketsSold}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                
                                <div className="top-performers-table">
                                    <h3>Top Performing Teams</h3>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Team Name</th>
                                                <th>Revenue</th>
                                                <th>Transactions</th>
                                                <th>Tickets Sold</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topTeams.map((team, index) => (
                                                <tr key={index}>
                                                    <td>{team.name}</td>
                                                    <td>{formatCurrency(team.revenue)}</td>
                                                    <td>{team.transactionCount}</td>
                                                    <td>{team.ticketsSold}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            
                            {/* Export Buttons */}
                            <div className="export-section">
                                <h3>Export Report</h3>
                                <div className="export-buttons">
                                    <button className="btn-export" onClick={() => handleShowExportModal('CSV')}>
                                        <FontAwesomeIcon icon={faDownload} /> Export as CSV
                                    </button>
                                    <button className="btn-export" onClick={() => handleShowExportModal('EXCEL')}>
                                        <FontAwesomeIcon icon={faDownload} /> Export as Excel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'report' && (
                        <div className="report-section">
                            {loading ? (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                    <p>Loading report...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="report-info">
                                        <p>Showing {reportData.content.length} of {reportData.totalElements} payments</p>
                                    </div>
                                    
                                    <div className="report-table-container">
                                        <table className="report-table">
                                            <thead>
                                                <tr>
                                                    <th>Order ID</th>
                                                    <th>IBO Number</th>
                                                    <th>IBO Name</th>
                                                    <th>Event</th>
                                                    <th>Total Charged</th>
                                                    <th>Net Amount</th>
                                                    <th>Status</th>
                                                    <th>Payment Method</th>
                                                    <th>Source</th>
                                                    <th>Tickets</th>
                                                    <th>Team</th>
                                                    <th>Upline Platinum IBO</th>
                                                    <th>Upline Platinum Name</th>
                                                    <th>Created At</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.content.map((payment) => (
                                                    <tr 
                                                        key={payment.id} 
                                                        onClick={() => handleRowClick(payment.id)}
                                                        className="clickable-row"
                                                    >
                                                        <td>{payment.orderId}</td>
                                                        <td>{payment.iboNumber}</td>
                                                        <td>{payment.iboName || 'N/A'}</td>
                                                        <td>{payment.eventName}</td>
                                                        <td className="amount">{formatCurrency(payment.total)}</td>
                                                        <td className="amount" style={{fontWeight: payment.processingFee ? 'bold' : 'normal'}}>
                                                            {formatCurrency(payment.netAmount || payment.total)}
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${payment.status.toLowerCase()}`}>
                                                                {payment.status}
                                                            </span>
                                                        </td>
                                                        <td>{payment.paymentMethod}</td>
                                                        <td>
                                                            <span className={`source-badge ${payment.purchaseSource.toLowerCase()}`}>
                                                                {payment.purchaseSource === 'ADMIN_PORTAL' ? 'Admin' : 'Customer'}
                                                            </span>
                                                        </td>
                                                        <td>{payment.totalTickets}</td>
                                                        <td>{payment.teamName || 'N/A'}</td>
                                                        <td>{payment.uplinePlatinumIbo || 'N/A'}</td>
                                                        <td>{payment.uplinePlatinumIboName || 'N/A'}</td>
                                                        <td>{formatDate(payment.createdAt)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    {/* Enhanced Pagination */}
                                    {reportData.totalPages > 1 && (
                                        <div className="pagination">
                                            <button
                                                className="pagination-btn"
                                                disabled={filters.page === 0}
                                                onClick={() => handlePageChange(filters.page - 1)}
                                            >
                                                Previous
                                            </button>
                                            
                                            <div className="page-numbers">
                                                {generatePageNumbers().map((page, index) => {
                                                    if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                                                        return (
                                                            <span key={`ellipsis-${index}`} className="page-ellipsis">
                                                                ...
                                                            </span>
                                                        );
                                                    }
                                                    return (
                                                        <button
                                                            key={page}
                                                            className={`page-number-btn ${filters.page === page ? 'active' : ''}`}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page + 1}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            
                                            <button
                                                className="pagination-btn"
                                                disabled={filters.page >= reportData.totalPages - 1}
                                                onClick={() => handlePageChange(filters.page + 1)}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
            
            {/* Payment Details Modal */}
            {showPaymentModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="payment-details-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Payment Details</h2>
                            <button className="close-btn" onClick={handleCloseModal}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        {loadingPaymentDetails ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>Loading payment details...</p>
                            </div>
                        ) : selectedPayment ? (
                            <div className="modal-content">
                                <div className="detail-section">
                                    <h3>Transaction Information</h3>
                                    <div className="detail-row">
                                        <span className="detail-label">Order ID:</span>
                                        <span className="detail-value">{selectedPayment.orderId}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Payment ID:</span>
                                        <span className="detail-value">{selectedPayment.paymentId || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Status:</span>
                                        <span className={`status-badge ${selectedPayment.status.toLowerCase()}`}>
                                            {selectedPayment.status}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Payment Method:</span>
                                        <span className="detail-value">{selectedPayment.paymentMethod}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Payment Provider:</span>
                                        <span className="detail-value">{selectedPayment.paymentProvider || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Purchase Source:</span>
                                        <span className={`source-badge ${selectedPayment.purchaseSource.toLowerCase()}`}>
                                            {selectedPayment.purchaseSource === 'ADMIN_PORTAL' ? 'Admin Portal' : 'Customer Website'}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="detail-section">
                                    <h3>Customer Information</h3>
                                    <div className="detail-row">
                                        <span className="detail-label">IBO Number:</span>
                                        <span className="detail-value">{selectedPayment.iboNumber}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">IBO Name:</span>
                                        <span className="detail-value">{selectedPayment.iboName || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Team:</span>
                                        <span className="detail-value">{selectedPayment.teamName || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Upline Platinum IBO:</span>
                                        <span className="detail-value">{selectedPayment.uplinePlatinumIbo || 'N/A'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Upline Platinum Name:</span>
                                        <span className="detail-value">{selectedPayment.uplinePlatinumIboName || 'N/A'}</span>
                                    </div>
                                </div>
                                
                                <div className="detail-section">
                                    <h3>Event Information</h3>
                                    <div className="detail-row">
                                        <span className="detail-label">Event:</span>
                                        <span className="detail-value">{selectedPayment.eventName}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Event Start Date:</span>
                                        <span className="detail-value">{formatEventDate(selectedPayment.eventStartDate, selectedPayment.eventTimezone)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Event End Date:</span>
                                        <span className="detail-value">{formatEventDate(selectedPayment.eventEndDate, selectedPayment.eventTimezone)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Total Tickets:</span>
                                        <span className="detail-value">{selectedPayment.totalTickets}</span>
                                    </div>
                                </div>
                                
                                <div className="detail-section">
                                    <h3>Payment Breakdown</h3>
                                    <div className="detail-row">
                                        <span className="detail-label">Amount:</span>
                                        <span className="detail-value">{formatCurrency(selectedPayment.amount)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Tax:</span>
                                        <span className="detail-value">{formatCurrency(selectedPayment.tax)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Service Charge:</span>
                                        <span className="detail-value">{formatCurrency(selectedPayment.serviceCharge)}</span>
                                    </div>
                                    <div className="detail-row total-row">
                                        <span className="detail-label">Total Charged:</span>
                                        <span className="detail-value">{formatCurrency(selectedPayment.total)}</span>
                                    </div>
                                    {selectedPayment.processingFee && selectedPayment.processingFee > 0 && (
                                        <>
                                            <div className="detail-row">
                                                <span className="detail-label">Processing Fee:</span>
                                                <span className="detail-value" style={{color: '#e74c3c'}}>
                                                    -{formatCurrency(selectedPayment.processingFee)}
                                                </span>
                                            </div>
                                            <div className="detail-row total-row" style={{borderTop: '2px solid #2c3e50', marginTop: '8px', paddingTop: '8px'}}>
                                                <span className="detail-label"><strong>Net Amount:</strong></span>
                                                <span className="detail-value"><strong>{formatCurrency(selectedPayment.netAmount || selectedPayment.total)}</strong></span>
                                            </div>
                                        </>
                                    )}
                                    <div className="detail-row">
                                        <span className="detail-label">Currency:</span>
                                        <span className="detail-value">{selectedPayment.currency}</span>
                                    </div>
                                </div>
                                
                                {selectedPayment.paymentMethod === 'CARD' && (selectedPayment.cardLast4 || selectedPayment.cardBrand) && (
                                    <div className="detail-section">
                                        <h3>Card Information</h3>
                                        {selectedPayment.cardBrand && (
                                            <div className="detail-row">
                                                <span className="detail-label">Card Brand:</span>
                                                <span className="detail-value">{selectedPayment.cardBrand}</span>
                                            </div>
                                        )}
                                        {selectedPayment.cardLast4 && (
                                            <div className="detail-row">
                                                <span className="detail-label">Last 4 Digits:</span>
                                                <span className="detail-value">**** **** **** {selectedPayment.cardLast4}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                <div className="detail-section">
                                    <h3>Timestamps</h3>
                                    <div className="detail-row">
                                        <span className="detail-label">Created At:</span>
                                        <span className="detail-value">{formatDate(selectedPayment.createdAt)}</span>
                                    </div>
                                    {selectedPayment.updatedAt && (
                                        <div className="detail-row">
                                            <span className="detail-label">Updated At:</span>
                                            <span className="detail-value">{formatDate(selectedPayment.updatedAt)}</span>
                                        </div>
                                    )}
                                </div>
                                
                                {selectedPayment.invoiceNumber && (
                                    <div className="detail-section">
                                        <h3>Invoice</h3>
                                        <div className="detail-row">
                                            <span className="detail-label">Invoice Number:</span>
                                            <span className="detail-value">{selectedPayment.invoiceNumber}</span>
                                        </div>
                                        <div className="invoice-actions">
                                            <button 
                                                className="btn-view-invoice" 
                                                onClick={handleDownloadInvoice}
                                                disabled={loadingPaymentDetails}
                                            >
                                                <FontAwesomeIcon icon={faExternalLinkAlt} /> 
                                                {loadingPaymentDetails ? 'Loading Invoice...' : 'Open Invoice in New Tab'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedPayment.receiptUrl && selectedPayment.paymentProvider === 'SQUARE' && (
                                    <div className="detail-section">
                                        <h3>Square Receipt</h3>
                                        <div className="detail-row">
                                            <span className="detail-label">Receipt URL:</span>
                                            <span className="detail-value">Available</span>
                                        </div>
                                        <div className="invoice-actions">
                                            <button 
                                                className="btn-view-invoice" 
                                                onClick={() => window.open(selectedPayment.receiptUrl, '_blank', 'noopener,noreferrer')}
                                            >
                                                <FontAwesomeIcon icon={faExternalLinkAlt} /> 
                                                Open Square Receipt
                                            </button>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedPayment.paymentProvider === 'SQUARE' && 
                                 (selectedPayment.paymentMethod === 'CARD' || selectedPayment.paymentMethod === 'SQUARE') && (
                                    <div className="detail-section">
                                        <h3>Processing Fees</h3>
                                        {selectedPayment.processingFee ? (
                                            <>
                                                <div className="detail-row">
                                                    <span className="detail-label">Total Fee:</span>
                                                    <span className="detail-value">
                                                        ${selectedPayment.processingFee} {selectedPayment.processingFeeCurrency}
                                                    </span>
                                                </div>
                                                {selectedPayment.feeBreakdown && Object.keys(selectedPayment.feeBreakdown).length > 0 && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Fee Breakdown:</span>
                                                        <div className="detail-value">
                                                            {Object.entries(selectedPayment.feeBreakdown).map(([type, details]) => (
                                                                <div key={type}>
                                                                    {type}: ${(details.amount / 100).toFixed(2)}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedPayment.feesFetchedAt && (
                                                    <div className="detail-row">
                                                        <span className="detail-label">Fees Last Updated:</span>
                                                        <span className="detail-value">{formatDate(selectedPayment.feesFetchedAt)}</span>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="detail-row">
                                                <span className="detail-label">Status:</span>
                                                <span className="detail-value">Not fetched yet</span>
                                            </div>
                                        )}
                                        <div className="invoice-actions">
                                            <button 
                                                className="btn-view-invoice" 
                                                onClick={() => handleRefreshFees(selectedPayment.id)}
                                                disabled={loadingPaymentDetails}
                                            >
                                                <FontAwesomeIcon icon={faSync} /> 
                                                {loadingPaymentDetails ? 'Refreshing...' : 'Refresh Fees from Square'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="error-message">
                                Failed to load payment details
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Event Details Modal - rendered via portal to escape sidebar layout */}
            {showEventDetailsModal && ReactDOM.createPortal(
                <div className="pa-event-modal-overlay" onClick={handleCloseEventDetailsModal}>
                    <div className="pa-event-details-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Event Details</h2>
                            <button className="close-btn" onClick={handleCloseEventDetailsModal}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        {loadingEventDetails ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>Loading event details...</p>
                            </div>
                        ) : selectedEventForDetails ? (
                            <div className="modal-content">
                                <div className="detail-section">
                                    <h3>Basic Information</h3>
                                    <div className="detail-row">
                                        <span className="detail-label">Event Name:</span>
                                        <span className="detail-value">{selectedEventForDetails.name}</span>
                                    </div>
                                    {selectedEventForDetails.description && (
                                        <div className="detail-row">
                                            <span className="detail-label">Description:</span>
                                            <span className="detail-value">{selectedEventForDetails.description}</span>
                                        </div>
                                    )}
                                    <div className="detail-row">
                                        <span className="detail-label">Status:</span>
                                        <span className={`status-badge ${selectedEventForDetails.status?.toLowerCase()}`}>
                                            {selectedEventForDetails.status}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="detail-section">
                                    <h3>Date & Time</h3>
                                    <div className="detail-row">
                                        <span className="detail-label">Start Date:</span>
                                        <span className="detail-value">{formatEventDate(selectedEventForDetails.startDate, selectedEventForDetails.timezone)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">End Date:</span>
                                        <span className="detail-value">{formatEventDate(selectedEventForDetails.endDate, selectedEventForDetails.timezone)}</span>
                                    </div>
                                    {selectedEventForDetails.timezone && (
                                        <div className="detail-row">
                                            <span className="detail-label">Timezone:</span>
                                            <span className="detail-value">{selectedEventForDetails.timezone}</span>
                                        </div>
                                    )}
                                </div>
                                
                                {selectedEventForDetails.venue && (
                                    <div className="detail-section">
                                        <h3>Location</h3>
                                        <div className="detail-row">
                                            <span className="detail-label">Venue:</span>
                                            <span className="detail-value">{selectedEventForDetails.venue.name}</span>
                                        </div>
                                        {selectedEventForDetails.venue.address && (
                                            <div className="detail-row">
                                                <span className="detail-label">Address:</span>
                                                <span className="detail-value">{selectedEventForDetails.venue.address}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                <div className="detail-section">
                                    <h3>Eligibility</h3>
                                    <div className="detail-row">
                                        <span className="detail-label">Eligible Teams:</span>
                                        <span className="detail-value">
                                            {Array.isArray(selectedEventForDetails.eligibleTeams)
                                                ? (selectedEventForDetails.eligibleTeams.includes('all') 
                                                    ? 'All Teams' 
                                                    : selectedEventForDetails.eligibleTeams.join(', '))
                                                : (selectedEventForDetails.eligibleTeams === 'all' 
                                                    ? 'All Teams' 
                                                    : selectedEventForDetails.eligibleTeams || 'N/A')}
                                        </span>
                                    </div>
                                    {selectedEventForDetails.minPinLevelRank != null && selectedEventForDetails.minPinLevelRank > 0 && (
                                        <div className="detail-row">
                                            <span className="detail-label">Min Pin Level Rank:</span>
                                            <span className="detail-value">{selectedEventForDetails.minPinLevelRank}</span>
                                        </div>
                                    )}
                                </div>
                                
                                {selectedEventForDetails.imageUrl && (
                                    <div className="detail-section">
                                        <h3>Event Image</h3>
                                        <img 
                                            src={selectedEventForDetails.imageUrl} 
                                            alt={selectedEventForDetails.name}
                                            className="event-image"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="error-message">
                                Failed to load event details
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
            
            {/* Export Field Selection Modal */}
            <ExportFieldSelectionModal
                show={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExport={handleExport}
                format={exportFormat}
            />
        </div>
    );
};

export default PaymentAuditPage;

